import { LoadingButton } from '@mui/lab'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, TextField } from '@mui/material'
import { GreenText, LabelField } from 'components'
import { DateField } from 'components/fields'
import { getOutboundTransporterCompanies } from 'controllers/companies/index'
import { OutboundTransporterCompanyDto } from 'dtos/companies'
import { OutboundTripDto } from 'dtos/outboundTrips'
import { Form, Formik } from 'formik'
import { useEffect, useState } from 'react'
import { OutboundTripDtoValidator } from 'validators/outboundTrips'

interface AddEditOutboundTripDialogProps {
  initialValues: OutboundTripDto
  isLoading: boolean
  onClose: () => void
  onSave: (values: OutboundTripDto) => void
  open: boolean
}

export default function AddEditOutboundTripDialog({ initialValues, isLoading, onClose, onSave, open }: AddEditOutboundTripDialogProps) {
  const [outboundTransporterCompanies, setOutboundTransporterCompanies] = useState<OutboundTransporterCompanyDto[]>([])

  useEffect(() => {
    getOutboundTransporterCompanies().then(res => {
      setOutboundTransporterCompanies(res.value)
    })
  }, [])

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={values => onSave(values)}
        validateOnBlur={false}
        validateOnChange={true}
        validationSchema={OutboundTripDtoValidator}
      >
        {({ errors, handleBlur, handleChange, setFieldValue, values }) => {
          return (
            <Form>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <DialogTitle>{values.outboundTripGuid ? 'Edit' : 'Add'} Outbound Trip</DialogTitle>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading}
                      error={Boolean(errors.outboundTripNumber)}
                      fullWidth
                      helperText={errors.outboundTripNumber}
                      label='Outbound Trip #'
                      name='outboundTripNumber'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.outboundTripNumber || ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <DateField
                      disabled={isLoading}
                      error={Boolean(errors.outboundTripDate)}
                      helperText={errors.outboundTripDate}
                      label='Outbound Trip Date'
                      onChange={value => setFieldValue('outboundTripDate', value)}
                      value={values.outboundTripDate}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading}
                      error={Boolean(errors.outboundTransporterCompany?.companyGuid)}
                      fullWidth
                      helperText={errors.outboundTransporterCompany?.companyGuid}
                      label='Outbound Transporter Company'
                      name='outboundTransporterCompany'
                      onBlur={handleBlur}
                      onChange={e => setFieldValue('outboundTransporterCompany', JSON.parse(e.target.value))}
                      select
                      value={
                        values.outboundTransporterCompany
                          ? JSON.stringify(outboundTransporterCompanies.find(otc => otc.companyGuid === values.outboundTransporterCompany.companyGuid))
                          : null
                      }
                    >
                      {outboundTransporterCompanies.map(otc => (
                        <MenuItem key={otc.companyGuid} value={JSON.stringify(otc)}>
                          {otc.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField
                      label='DOT #'
                      value={values?.outboundTransporterCompany?.dotNumber ?? values?.outboundTransporterCompanyDotNumber ?? undefined}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <GreenText variant='outlined'>
                      {`**Outbound Trip # Field:**

A free-type field that allows the user enter the Outbound Trip Number.

This field is required.

**Outbound Trip Date Field:**

A date field that allows the user to enter the date for the Outbound Trip.

This field is required.

**Outbound Transporter Company Drop-Down:**

A Drop-down field that displays all Active Companies that have the “Outbound Transporter Company” toggle enabled under the Edit Company.

This field is required.

**DOT # Label:**
 
Displays the DOT # that is automatically populated based on the selected Outbound Transporter Company.

The DOT # Is retrieved from Company Details – Transporter Information.

**Save Button:**

Clicking this button will save all values entered and create/update the Outbound Trip.

**Cancel Button:**

Clicking the button will close the dialog and not save any values entered.`}
                    </GreenText>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button color='inherit' disabled={isLoading} onClick={onClose} variant='text'>
                  CANCEL
                </Button>

                <LoadingButton loading={isLoading} type='submit' variant='contained'>
                  SAVE
                </LoadingButton>
              </DialogActions>
            </Form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
